<template>
  <WdsAlert
    icon
    class="wds-alert-grey tw-mb-5"
    :title="
      $t(
        'Worksome will send a POST request with a payload of event data in the request body (formatted in JSON)',
      )
    "
    :description="
      $t(
        'Your service must respond with a 200 or 201 status code for the request to be considered successful',
      )
    "
  />
  <WdsStack direction-column gap-lg class="tw-w-full" v-bind="$attrs">
    <WdsStack direction-column gap-sm class="tw-w-full">
      <WdsText :text="$t('Name')" :sub-text="$t('What this webhook is used for')" />
      <WdsInput
        v-model="input.title"
        :error="validationText.title"
        :placeholder="$t('Update internal job board')"
        data-test="modal-webhook-title"
      />
    </WdsStack>
    <WdsStack direction-column gap-sm class="tw-w-full">
      <WdsText
        :text="$t('Endpoint URL')"
        :sub-text="$t(`Your service's secure URL, listening for requests`)"
        data-test="modal-webhook-endpoint"
      />
      <WdsInput
        v-model="input.url"
        :error="validationText.url"
        :disabled="loading"
        placeholder="https://your.inboundplatform.domain"
        data-test="modal-webhook-url"
      />
    </WdsStack>
    <WdsStack direction-column gap-sm class="tw-w-full">
      <WdsText
        :text="$t('Events')"
        :sub-text="$t('Select which events should trigger this webhook')"
      />
      <WdsStack gap-sm direction-column>
        <WdsCheckbox
          :checked="allEventsSelected"
          :indeterminate="someEventsSelected && !allEventsSelected"
          :disabled="loading || allEventsSelected"
          data-test="modal-webhook-events-all"
          @update:checked="toggleAllEvents"
        >
          <WdsText
            :text="$t('All events')"
            :sub-text="
              allMeansNull
                ? $t('Subscribe to all current and future event types')
                : $t('Subscribe to all event types')
            "
          />
        </WdsCheckbox>
        <div class="events-list tw-ml-6 tw-border-l tw-border-gray-200 tw-pl-4">
          <WdsStack direction-column gap-xs>
            <WdsCheckbox
              v-for="eventType in availableEventTypes"
              :key="eventType.value"
              :checked="isEventSelected(eventType.value)"
              :disabled="loading || isLastSelectedEvent(eventType.value)"
              :data-test="`modal-webhook-event-${eventType.value}`"
              @update:checked="(checked) => toggleEvent(eventType.value, checked)"
            >
              <WdsText :text="eventType.label" />
            </WdsCheckbox>
          </WdsStack>
        </div>
      </WdsStack>
    </WdsStack>
    <WdsStack direction-column gap-sm class="tw-w-full">
      <WdsText
        :text="$t('Authentication')"
        :sub-text="
          $t(
            'Select an authentication method. The secret will only be visible before it is saved; once saved, it will not be displayed again.',
          )
        "
      />
      <WdsStack class="webhook-auth-form" gap-sm direction-column>
        <WdsSegmentedControl
          :options="segmentedOptions"
          :default-active-key="activeKey"
          full-width
          @click="changeAuthenticationMethodForm"
        />
        <template v-if="activeKey === secretKeySegment">
          <WdsInput
            v-model="input.secret"
            :disabled="loading"
            :placeholder="containsSecret ? '•••••••••••••' : $t('A_UNIQUE_SECRET')"
            :info="
              $t(
                'Secret is used to create a hash of the payload. The hash is passed in the Signature header. Your service should attempt to verify this signature by creating the same hash with the content received.',
              )
            "
            :error="validationText.secret"
            data-test="modal-webhook-secret"
          />
        </template>
        <template v-if="activeKey === OAuthKeySegment">
          <WdsInput
            v-model="input.clientSecret"
            :error="validationText.clientSecret"
            :disabled="loading"
            :placeholder="containsClientSecret ? '•••••••••••••' : $t('A_UNIQUE_SECRET')"
            :label="$t('Client Secret')"
            data-test="modal-webhook-client-secret"
          />
          <WdsInput
            v-model="input.clientId"
            :error="validationText.clientId"
            :disabled="loading"
            :placeholder="$t('admin')"
            :label="$t('Client ID')"
            data-test="modal-webhook-client-id"
          />
          <WdsInput
            v-model="input.clientUrl"
            :error="validationText.clientUrl"
            :disabled="loading"
            placeholder="https://your.inboundplatform.domain/oauth/token"
            :label="$t('Access Token URL')"
            :info="
              $t(
                'The token received from your access token endpoint using the Client ID and Client Secret will be sent in the Authorization header. Your service should verify that the token sent is valid.',
              )
            "
            data-test="modal-webhook-client-url"
          />
        </template>
      </WdsStack>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsAlert,
  WdsCheckbox,
  WdsInput,
  WdsSegmentedControl,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { computed, ComputedRef } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { convertEnumToDisplayText, textHelpers } from '@/utils/tools'
import { WebhookEventType } from '@/gql/graphql'
import {
  SUPPLIER_NULL_SUBSCRIPTION_EVENT_TYPES,
  SUPPLIER_WEBHOOK_EVENT_TYPES,
} from '@/components/Integrations/Webhooks/types'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

interface ModifiableWebhookInput {
  title: string
  description?: string | null
  url: string
  secret?: string | null
  isActive: boolean
  subscribedEvents?: WebhookEventType[] | null
  clientId?: string | null
  clientSecret?: string | null
  clientUrl?: string | null
}

const input = defineModel<ModifiableWebhookInput>('input', { required: true })
const validationErrors = defineModel<Record<string, ComputedRef<string>>>('validationErrors')

const { isRecruiter } = useAccountCapabilities()

// A staffing agency webhook without a list misses the hire events, so "all" is sent as a list.
const allMeansNull = !isRecruiter.value

const allEventValues = isRecruiter.value
  ? SUPPLIER_WEBHOOK_EVENT_TYPES
  : Object.values(WebhookEventType)

const availableEventTypes = allEventValues.map((value) => ({
  value,
  label: $t(convertEnumToDisplayText(value)),
}))

// What the webhook receives, including when it has no list
const selectedEvents = computed(
  () =>
    input.value.subscribedEvents ??
    (allMeansNull ? allEventValues : SUPPLIER_NULL_SUBSCRIPTION_EVENT_TYPES),
)

const allEventsSelected = computed(() =>
  allEventValues.every((event) => selectedEvents.value.includes(event)),
)

const someEventsSelected = computed(() => selectedEvents.value.length > 0)

const isEventSelected = (eventValue: WebhookEventType): boolean =>
  selectedEvents.value.includes(eventValue)

// Check if unchecking this event would leave zero events selected
const isLastSelectedEvent = (eventValue: WebhookEventType): boolean =>
  selectedEvents.value.length === 1 && selectedEvents.value.includes(eventValue)

const toggleAllEvents = () => {
  // Unchecking "all" is not allowed: at least one event stays selected
  input.value.subscribedEvents = allMeansNull ? null : [...allEventValues]
}

const toggleEvent = (eventValue: WebhookEventType, checked: boolean) => {
  const selected = selectedEvents.value

  if (checked) {
    input.value.subscribedEvents = selected.includes(eventValue)
      ? [...selected]
      : [...selected, eventValue]
  } else {
    // Prevent unchecking the last event
    if (selected.length <= 1) {
      return
    }
    input.value.subscribedEvents = selected.filter((e) => e !== eventValue)
  }

  // If all events are now selected, set back to null for backward compatibility
  if (allMeansNull && allEventsSelected.value) {
    input.value.subscribedEvents = null
  }
}

const map = {
  Title: 'Name',
  ClientSecret: 'Client secret',
  ClientId: 'Client id',
  ClientUrl: 'Token url',
  Url: 'Endpoint url',
  'input.url': 'endpoint url',
  'input.client url': 'token url',
  'input.secret': 'secret',
  'input.client id': 'client secret',
  'input.title': 'name',
  'input.client secret': 'client secret',
}
const cleanText = (t) => {
  if (typeof t !== 'string') return false
  let text = t

  Object.keys(map).forEach((key) => {
    text = text.replace(key, map[key])
  })

  return textHelpers.capitalizeFirstLetter(text)
}

const validationText = computed<Record<string, string | boolean>>(() =>
  Object.keys(validationErrors.value ?? {}).reduce(
    (obj, key: string) => ({
      ...obj,
      [key]: cleanText(validationErrors.value?.[key].value),
    }),
    {},
  ),
)

const orgOAuthClient = {
  clientId: input.value?.clientId,
  clientUrl: input.value?.clientUrl,
}

const containsSecret = defineModel<boolean>('containsSecret', { default: false })
const containsClientSecret = defineModel<boolean>('containsClientSecret', { default: false })
const loading = defineModel<boolean>('loading')

const secretKeySegment = 'SECRET_KEY' as const
const OAuthKeySegment = 'OAUTH' as const

const activeKey = defineModel<'SECRET_KEY' | 'OAUTH'>('activeKey', { default: secretKeySegment })

const segmentedOptions = computed(() => [
  {
    optionKey: secretKeySegment,
    title: $t('Secret key'),
    disabled: loading.value,
  },
  {
    optionKey: OAuthKeySegment,
    title: $t('OAuth Client'),
    disabled: loading.value,
  },
])

if (containsClientSecret.value) {
  activeKey.value = OAuthKeySegment
  input.value.clientSecret = null
} else {
  activeKey.value = secretKeySegment
  if (containsSecret.value) {
    input.value.secret = null
  }
}
const changeAuthenticationMethodForm = (authType: 'SECRET_KEY' | 'OAUTH') => {
  if (loading.value) return

  if (authType === OAuthKeySegment) {
    Object.assign(input.value, orgOAuthClient)
    input.value.secret = null
  } else {
    input.value.clientId = null
    input.value.clientSecret = null
    input.value.clientUrl = null
  }

  activeKey.value = authType
}
</script>

<style scoped lang="scss">
.wds-alert-grey {
  background-color: #fafafa;
  border-color: #dfdfe6;
  &:deep(.wds-alert-title) {
    color: #353342;
  }

  &:deep(.wds-alert-icon-container) {
    fill: #777787;
    stroke: #777787;
  }
}

.events-list {
  max-height: 140px; /* Approximately 5 items */
  overflow-y: scroll;
}
</style>
